import { canonicalJson } from '@djgg/gameplay-kit';
import { isCompositionSuccessorV1 } from '../compositionLineage.js';
import type { AcceptedCompositionV1 } from '../spec/gameSpec.js';
import type { AssetGenJob, AssetVariantDoc, BuildJob, CreationVersion } from '../types.js';
import { validateCompositionRoute } from './compositionRoute.js';

/** Whether a journal row can be reused by the child `childJobId`. Its own
 * candidates are staged until it publishes. A predecessor's row is staged when
 * that child failed, or active when that child succeeded and adopted it —
 * every journal job belongs to a FAILED version, so neither was published live. */
export function isReusableCompositionJournalRowV1(row: Pick<AssetVariantDoc, 'exposureState' | 'genJobId'>,
  childJobId: string): boolean {
  return row.exposureState === 'staged' || (row.exposureState === 'active' && row.genJobId !== childJobId);
}

/** Bound every immutable retry journal read and its transaction lock set. */
export const MAX_VISUAL_RESUME_CHAIN = 20;

export interface CompositionMediaResumeReader {
  getMediaJob(id: string): Promise<(AssetGenJob & { id?: string }) | null>;
  getBuildJob(id: string): Promise<BuildJob | null>;
  getVersion(creationId: string, version: number): Promise<CreationVersion | null>;
}

type CompositionMediaResumeChild = Pick<AssetGenJob,
  'creationId' | 'createdBy' | 'creationRun' | 'plan' | 'measuredBudget'> & { id: string };

/** Validate the immutable media chain used by a composed retry. Runtime
 * releases may change between attempts, but every predecessor must be the
 * exact same accepted game on an older runtime and must still be the terminal
 * visual child named by its own failed version. A succeeded child counts: its
 * parent failed after it (e.g. at publication), so its staged slots were never
 * published and remain this game's paid, reusable work. */
export async function compositionMediaResumeJournal(
  reader: CompositionMediaResumeReader,
  child: CompositionMediaResumeChild,
  currentAuthority: AcceptedCompositionV1,
): Promise<string[]> {
  const journal = [child.id];
  let next = child.plan?.resumeFromJobId;
  let newerVersion = child.creationRun?.version ?? Number.MAX_SAFE_INTEGER;
  for (let depth = 0; next && depth < MAX_VISUAL_RESUME_CHAIN; depth += 1) {
    const priorId = next;
    const prior = await reader.getMediaJob(priorId);
    const buildJobId = prior?.measuredBudget?.buildJobId;
    const priorVersionNumber = prior?.creationRun?.version;
    const parent = buildJobId ? await reader.getBuildJob(buildJobId) : null;
    const version = priorVersionNumber === undefined
      ? null
      : await reader.getVersion(child.creationId, priorVersionNumber);
    let priorAuthority: AcceptedCompositionV1 | null = null;
    if (parent && version && buildJobId) {
      try {
        const route = await validateCompositionRoute(parent, version, buildJobId);
        if (!route.lineage) priorAuthority = route.authority;
      } catch {
        priorAuthority = null;
      }
    }
    if (!prior || (prior.status !== 'failed' && prior.status !== 'succeeded') || prior.creationId !== child.creationId || journal.includes(priorId)
      || prior.createdBy !== child.createdBy || prior.creationRun?.branch !== 'visual'
      || prior.creationRun.designRev !== child.creationRun?.designRev
      || prior.creationRun.version >= newerVersion
      || prior.plan?.orientation !== child.plan?.orientation
      || canonicalJson([...(prior.plan?.assetNames ?? [])].sort())
        !== canonicalJson([...(child.plan?.assetNames ?? [])].sort())
      || !prior.measuredBudget || !child.measuredBudget
      || prior.measuredBudget.role !== child.measuredBudget.role
      || (prior.measuredBudget.role !== undefined && prior.measuredBudget.role !== 'composition-media@2')
      || prior.measuredBudget.executionDigest !== child.measuredBudget.executionDigest) {
      throw new Error('Composition media resume journal is outside its accepted authority (job)');
    }
    if (!parent || parent.creationId !== child.creationId || parent.version !== prior.creationRun.version
      || !version || version.buildStatus !== 'failed'
      || version.creationRun?.visualJobId !== priorId || version.creationRun.codeJobId !== buildJobId
      || !priorAuthority || prior.measuredBudget.authorityDigest
        !== (prior.measuredBudget.role === 'composition-media@2' ? priorAuthority.digest : undefined)) {
      throw new Error('Composition media resume journal is outside its accepted authority (route)');
    }
    if (!await isCompositionSuccessorV1(currentAuthority, priorAuthority)) {
      throw new Error('Composition media resume journal is outside its accepted authority (game)');
    }
    journal.push(priorId);
    newerVersion = prior.creationRun.version;
    next = prior.plan?.resumeFromJobId;
  }
  if (next) throw new Error('Composition media resume journal is too long');
  return journal;
}
